import React from 'react'
import { Grid, Container } from '@mui/material';
import * as Assets from './assets'
import './NeedfindingGame.css'
// Import Swiper styles
import { Navigation, Pagination, Scrollbar, A11y } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/swiper-bundle.min.css'
import 'swiper/swiper.min.css'
//spell-checker:ignore gifs, lofi, hifi

export function Intro() {
  return (
    <div id="Intro">
      <h1 data-aos="fade-up"><span className="project-title monospace-300">Modeling social exclusion and isolation with a game</span></h1>
      <h2 data-aos="fade-up" data-aos-delay="100" className="project-subheading monospace-200">How can we immerse people with the feeling of social isolation and exclusion and bring attention to how it could be disrupted?</h2>
      <img data-aos="fade-up" data-aos-delay="300" src={Assets.PTFinalHero2} alt="what2yum features" loading="lazy" style={{marginTop: "30px"}}></img>
      <Grid data-aos="fade-up" data-aos-delay="400" container columns={4} columnSpacing={1} style={{marginTop: "30px"}}>
        <Grid item xs={2} md={1}>
          <h2>role</h2>
          <p className="project-description">
            Game Designer<br></br>
            Product Designer
          </p>
        </Grid>
        <Grid item xs={2} md={1}>
          <h2>team</h2>
          <p className="project-description">
            Wei Lin Puah
          </p>
        </Grid>
        <Grid item xs={2} md={1}>
          <h2>tools</h2>
          <p className="project-description">
            Figma<br></br>
            Paper & Pencil<br></br>
            Cardboard<br></br>
          </p>
        </Grid>
        <Grid item xs={2} md={1}>
          <h2>skills</h2>
          <p className="project-description">
            game design<br></br>
            needfinding<br></br>
            playtesting<br></br>
          </p>
        </Grid>
      </Grid>
      <div className="text-divider">
        <img className="leaf-divider" src={process.env.PUBLIC_URL + '/leaf-divider.png'} alt='leaf divider'/>
      </div>
    </div>
  )
}

export function Inspiration() {
  return (
    <div id="Inspiration" className="project-section">
      <h1><span className="section-title highlight">inspiration</span></h1>
      <p>
        Growing up as an introvert, I have experienced social isolation and exclusion time and time again. Even now as an adult, I feel it when I'm the only one at the table clueless about the topic being discussed. "Is it wrong for me to try to add to the conversation when it doesn't make sense? Should I just sit quietly and wait for this conversation to be over? I feel like I need to laugh along even though I don't know why it's funny. Can we talk about something else?"
      </p>
      <p>
        Through a game, we wanted to model these isolating feelings to help others understand what it may feel like, especially when you're not in "the know". At the same time, we wanted to disrupt this social system by showing people that they can create inclusive environments where everyone feels like they can contribute, and it's more fun when everyone is involved.
      </p>
    </div>
  )
}

export function Playtest1() {
  return (
    <div id="Playtest1" className="project-section">
      <h1><span className="section-title highlight">playtest v1</span></h1>
      <p>
        For the first round of playtesting, we really focused on trying to first create the feeling of isolation, self-doubt, and discomfort for the person being isolated and the feeling of fun and collaboration for everyone else. We were curious to see if natural behaviors of inclusion or exclusion would come up. We landed on a puzzle game to mirror the system. In puzzles, you're often collaborating towards a common goal of finishing the puzzle, but we were curious to see what would happen if someone gets pieces that doesn't quite fit&mdash;just like in real life when someone's knowledge or context doesn't quite fit with everyone else's. 
      </p>
      <p>
        We had a couple of variations of the game that we wanted to playtest: allowing talking vs no talking, passing the board around vs having the board in the middle, and having puzzle pieces that are clearly different vs just a little bit different.
      </p>
      <Grid container columns={3} columnSpacing={4} rowSpacing={4} style={{marginTop: "0px", marginBottom: "40px"}}>
        <Grid item xs={2} md={1}>
          <img src={Assets.PT1action3} alt="playtest1" loading="lazy"></img>
        </Grid>
        <Grid item xs={2} md={1}>
          <img src={Assets.PT1action4} alt="playtest1" loading="lazy"></img>
        </Grid>
        <Grid item xs={2} md={1}>
          <img src={Assets.PT1action2} alt="playtest1" loading="lazy"></img>
        </Grid>
      </Grid>
      <p>
        For the first round, we put the frame in the center and allowed talking. One person got pixelated pieces instead of full resolution. At first, there was lots of discussion on whether to include the different puzzle pieces, with some saying "this one doesn't fit" and "Do we want to include Michael (player with the different pieces)?". An interesting behavior that emerged pretty quickly was people swapping pieces and an arriving at a consensus to fit the different puzzle pieces into the puzzle by overlapping pieces and creating an "artistic rendering".
      </p>
      <p>
        For the second round, we enforced no talking and passing the frame around. One person got black and white pieces instead of colored. Since it's the players' second time playing, the flow of the game started with swapping pieces immediately even with no talking. The flow of the frame naturally went in sequential order, but ended up in the middle again at some point towards the middle of the game. There was lots of visual gesturing and the person with black and white pieces was actively participating as well. When someone ran out of pieces, someone even gave them one to include them back in the game.
      </p>
      <p>
        Although it was very heartwarming to see all the interactions, it seemed that we got to the disruption of the system, or the system we want to see before we got to immerse them in the system of social isolation. Some of the other feedback we got was:
      </p>
      <p>
        <ul>
          <li>having the frame shared was more comfortable versus doing it on your own</li>
          <li>it felt like it was meant to be a competition but it wasn't”</li>
          <li>add rules to make it more competitive or make it team-based</li>
          <li>use different puzzle pieces that just cannot fit at all</li>
        </ul>
      </p>

    </div>
  )
}

export function Playtest2() {

  return (
    <div id="Playtest2">
      <h1><span className="section-title highlight">playtest v2</span></h1>
      <p>
        For the second iteration of the game, we focused on bringing out the feeling of isolation with the game. We wanted test what would happen if one person had pieces that didn't fit at all into the board. Would the person sit there in silence? Would the person feel confused? Would they feel isolated when everyone else is placing their pieces in the middle? We introduced a points system to incentivize players not to be cooperative to begin with. Then, to show that the misfit pieces actually had a place all along, us / the gamemasters would introduce a secret second round where the puzzle expands from a boring brown square to a colorful sunflower. The key is that this unlocking of the secret round lies in one person's hands to take the frame off the puzzle. We were trying to model how in real life, often it just takes one person to notice then shift the conversation to include someone. In this secret round, the puzzle rules and pieces are also in the middle instead of everyone having their own pieces and rules. 
      </p>

      <p className="subheading">alignment with social isolation</p>
      <Grid container columns={3} columnSpacing={4} rowSpacing={4} style={{marginTop: "0px", marginBottom: "40px"}}>
        <Grid item xs={2} md={1}>
          <img src={Assets.PT2rules1} alt="playtest2" loading="lazy"></img>
        </Grid>
        <Grid item xs={2} md={1}>
          <img src={Assets.PT2action} alt="playtest2" loading="lazy"></img>
        </Grid>
        <Grid item xs={2} md={1}>
          <img src={Assets.PT2action2} alt="playtest2" loading="lazy"></img>
        </Grid>
      </Grid>

      <p className="subheading">disruption of social isolation</p>
      <Grid container columns={3} columnSpacing={4} rowSpacing={4} style={{marginTop: "0px", marginBottom: "40px"}}>
        <Grid item xs={2} md={1}>
          <img src={Assets.PT2reflection} alt="playtest2" loading="lazy"></img>
        </Grid>
        <Grid item xs={2} md={1}>
          <img src={Assets.PT2reflectionspecial} alt="playtest2" loading="lazy"></img>
        </Grid>
        <Grid item xs={2} md={1}>
          <img src={Assets.PT2action4} alt="playtest2" loading="lazy"></img>
        </Grid>
        <Grid item xs={2} md={1}>
          <img src={Assets.PT2action3} alt="playtest2" loading="lazy"></img>
        </Grid>
        <Grid item xs={2} md={1}>
          <img src={Assets.PT2rules2} alt="playtest2" loading="lazy"></img>
        </Grid>
      </Grid>
      <p>
        With this second round, we found that despite the points system, players were still very cooperative in fixing the puzzle. The person who got the misfit pieces started naturally adding them to the outside of the frame and taking pieces from other players to put in the middle. One of our users mentioned that "puzzles are collaborative in nature, not something to win." This insight stuck with us in our final iteration of the game. Other players didn't feel a huge shift between the first and second secret rounds of the game since they didn't feel anyone was isolated to begin with. 
      </p>
    </div>
  )
}

export function FinalPlaytest() {
  return (
    <div id="FinalPlaytest" className="project-section">
      <h1><span className="section-title highlight">final playtest</span></h1>
      <p>
        With the final playtest, we pivoted to a word game, as that most closely relates to being socially isolated in a conversation. But, we held onto our puzzle concept for the disruption. 
      </p>
      <p>
        Our game is a topic matching word game where players have to put down words that match the topic in the middle and explain why it fits. However, one person will get words that clearly are different from the topic whereas everyone else will get words that mostly align. We had an idea to pick a pretty niche topic, The Loft (the Master' students' design studio), so that the person who isn't in this group will almost automatically feel excluded from the start. The person to get rid of all their cards first wins the round. Everyone else gets to judge whether they agree that the word and explanation fits the topic.
      </p>
      <p>
        After one person won, we asked everyone how they were feeling after that round to help them reflect on their experience. For the second round, the surprise is that the back of the cards actually makes up a puzzle. So to win, they complete the puzzle together. The puzzle shows all the different words that showed up in the game, with the thought that as they put the pieces together, they will explain further what everything is and bring the isolated person into their world while creating a new picture together.
      </p>
      <Grid container columns={2} columnSpacing={4} rowSpacing={4} style={{marginTop: "0px", marginBottom: "40px"}}>
        <Grid item xs={2} md={1}>
          <img src={Assets.PTFinalRule} alt="playtestfinal" loading="lazy"></img>
        </Grid>
        <Grid item xs={2} md={1}>
          <img src={Assets.PTFinalRule2} alt="playtestfinal" loading="lazy"></img>
        </Grid>
        <Grid item xs={2} md={1}>
          <img src={Assets.PTFinalRule3} alt="playtestfinal" loading="lazy"></img>
        </Grid>
        <Grid item xs={2} md={1}>
          <img src={Assets.PTFinalGamebox} alt="playtestfinal" loading="lazy"></img>
        </Grid>
        <Grid item xs={2} md={1}>
          <img src={Assets.PTFinal2} alt="playtestfinal" loading="lazy"></img>
        </Grid>
        <Grid item xs={2} md={1}>
          <img src={Assets.PTFinal} alt="playtestfinal" loading="lazy"></img>
        </Grid>
        <Grid item xs={2} md={1}>
          <img src={Assets.PTFinalLive1} alt="playtestfinal" loading="lazy"></img>
        </Grid>
        <Grid item xs={2} md={1}>
          <img src={Assets.PTFinalLive2} alt="playtestfinal" loading="lazy"></img>
        </Grid>
        <Grid item xs={2} md={1}>
          <img src={Assets.PTFinalLive3} alt="playtestfinal" loading="lazy"></img>
        </Grid>
      </Grid>
      <p>
        With this final playtest, the person who wasn't part of the Masters in Design cohort indeed felt isolated. She wished there was a version of this game for the Med school, where she is a part of. At some point, she stopped trying to put her cards in the middle and just watched everyone else's turns. She noticed that her cards were more difficult than the rest, but for the rest of the people, they didn't notice her having difficulty with putting cards down. That was interesting as this often happens in real life where people don't notice that someone doesn't feel included.
      </p>
      <p>
        As for the second round, there didn't end up being much conversation around the drawings or topic itself because much of it was explained in the previous round. The puzzle felt collaborative, calm, and healing.
      </p>
      <p>
        It seemed that for this final round, most of the feedback came from the connection between the first and second rounds. Some found it a bit abrupt and we also got some feedback that a stronger approach would be to collectively make a new puzzle or drawing rather than having the puzzle still be about The Loft.
      </p>
    </div>
  )
}

export function LessonsNextSteps() {
  return (
    <div id="LessonsNextSteps" className="project-section">
      <h1><span className="section-title highlight">lessons & next steps</span></h1>
      <p>
        I found this process of creating a game that modeled and disrupted a system extremely challenging. It was not only hard to design a game for the first time, but to create one that wasn't necessarily fun but immersive into a system meant that I had to really hone in on the feeling I wanted players to come away with. I learned that a lot of times, what we simulate in our heads is often not the result of actually playtesting with real people. Furthermore, because everyone has experience with the system of social isolation and exclusion in different ways, everyone will also interact with the game differently. Someone who got the different puzzle pieces was very sociable and extroverted, and thus had no problem sharing pieces and taking from others' piles. However, someone more introverted like myself may have just ended up sitting there watching others play. It was super interesting to stress test the game in a way that would allow anyone to feel a sense of social isolation, not just those who are more introverted.
      </p>
      <p>
        In our case, it was also hard to figure out how to immerse one person only into social isolation and have everyone else be immersed in the world of not noticing that someone is isolated. I think we got pretty close with our last round of playtesting, but our transition into the re-alignment of everyone's point of views to an inclusive and generative one could be smoother.
      </p>
      <p>
        If we were to do one more iteration, we would want to focus on what is shown on the puzzle. Is it a free-for-all puzzle where they can draw something together as a puzzle? Is it an image of something they all have in common (such as the class they are taking together)? What would happen if we tested different topics on different sets of people? If anything, I learned how crucial playtesting is to get unstuck, prove or disprove theories, and get new ideas flowing.
      </p>
    </div>

  )
}

